import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useEvent, useMsci, useBooking, useAddOns } from '@/store';
import { useLazyGetInstallmentsByBinQuery } from '@/store/msci/msci.api';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import { trace } from '@/utils/tracingLogs';
import { isAE } from '@/utils/creditCardType';
import useSummary from '@/store/summary/useSummary';

const verifyBinLenTrheshold = 8;

const paymentTypes = {
  'mci': 'meses_con_intereses',
  'msi': 'meses_sin_intereses',
  'flat payment': 'contado',
};

const shouldTrace = false;

export default function useMsciControl() {
  const [temporalBin, setTemporalBin] = useState('');
  const [showSelector, setShowSelector] = useState(false);
  const [isLanding, setIsLanding] = useState(true);

  const { setIsLoadingSummary } = useSummary();

  const router = useRouter();

  const {
    cardAvailableForInstallments,
    cardNotAvailbleForInstallments,
    paymentOptionSelected,
  } = useAnalyticsEvents();

  const booking = useBooking();
  const { ticketInsurance } = useAddOns();
  const {
    setBin,
    setBinSelected,
    setPlans,
    setPreSaleBinValid,
    showPreSaleBinError,
    resetPlans,
    selectPaymentPlan,
    shouldCallSummary,
    resetAndCleanSelected,
    handleShowed,
    msci,
  } = useMsci();

  const [binTrigger] = useLazyGetInstallmentsByBinQuery();

  const {
    event: {
      event: { id },
      gateway,
    },
  } = useEvent();

  const filterByKind = (data, kind) => {
    const plans = data.filter((item) => item.payment_type === kind);

    return plans.length > 0
      ? plans.sort((a, b) => a.payment_plan - b.payment_plan)
      : null;
  };

  const verifyBin = (cardNumber) => {
    trace({ cardNumber }, shouldTrace);
    trace({ temporalBin }, shouldTrace);

    const threshold = isAE(cardNumber) ? 6 : verifyBinLenTrheshold;

    if (temporalBin && cardNumber?.substring(0, threshold) === temporalBin) {
      trace(
        'El bin es el mismo que el almacenado previamente, no se realiza acción',
        shouldTrace,
      );
      return;
    }

    if (!cardNumber || cardNumber?.length < threshold) {
      trace(
        'La tarjeta se borro o los diigtos no son suficientes se debe reiniciar los planes almacenados y seleccionado',
        shouldTrace,
      );
      shouldCallSummary();
      resetPlans();
      setTemporalBin('');
      showPreSaleBinError(false);

      return;
    }

    trace(
      'La tarjeta ingresada es valida, se envia a validar se tiene planes disponibles',
      shouldTrace,
    );
    setTemporalBin(cardNumber?.substring(0, threshold));
  };

  const validateBin = async () => {
    trace(
      'inicia la validación de bin para planes disponibles...',
      shouldTrace,
    );
    if (msci.availableBin && temporalBin === msci.availableBin) {
      trace(
        'Ya existe un bin verificado y es el mismo que se mando a verificar',
        shouldTrace,
      );
      return;
    }

    if (temporalBin !== msci.availableBin) {
      trace(
        'Es un nuevo bin, limpado planes y reseteando el seleccionado previamente',
        shouldTrace,
      );

      resetPlans();
    }

    trace(`Solicitando data para el bin: ${temporalBin}`, shouldTrace);
    const { data } = await binTrigger({ bin: temporalBin, id });

    if (data?.length < 1 || !data) {
      trace('No hay planes disponibles', shouldTrace);
      cardNotAvailbleForInstallments();
      resetPlans();
      trace('Planes reseteados');
      if (msci?.touched) shouldCallSummary();
      if (msci?.presale) showPreSaleBinError(true);
      return;
    }

    trace(`nuevo bin ingresado: ${temporalBin}`, shouldTrace);
    setBin(temporalBin);
    // setBinSelected(data[0].bin);
    setBinSelected(temporalBin);

    shouldCallSummary();
    trace(
      'Inicia llamada para obtener planes en base al total de la compra',
      shouldTrace,
    );
  };

  const setAvailablePlans = (plans) => {
    if (router.pathname !== '/payment') return;
    trace(`Respuesta con planes: `, shouldTrace, plans);
    if (plans?.length < 1 && !isLanding) {
      trace('No hay planes disponibles. Resetando planes', shouldTrace);
      resetPlans();
      return;
    }

    if (plans.some((plan) => plan.payment_type !== 'flat payment')) {
      cardAvailableForInstallments();
    } else {
      cardNotAvailbleForInstallments();
    }

    const payload = {
      msiAvailablePlans: filterByKind(plans, 'msi'),
      mciAvailablePlans:
        gateway === 'ORKESTA_PAY' ? filterByKind(plans, 'mci') : null,
      defaultPaymentPlan: filterByKind(plans, 'flat payment'),
    };

    trace(`seteando nuevos planes: `, shouldTrace, payload);
    setPlans(payload);
    setIsLanding(false);
  };

  const handleSelectedPlan = (value) => {
    const [amount, kind] = value.split('-');
    const { msiAvailablePlans, mciAvailablePlans, defaultPaymentPlan } = msci;
    if (parseInt(amount) === 0) {
      selectPaymentPlan({ selected: defaultPaymentPlan[0] });
      shouldCallSummary();
      setIsLoadingSummary(true);
      return;
    }

    let plans = null;

    if (kind === 'msi') plans = msiAvailablePlans;
    if (kind === 'mci') plans = mciAvailablePlans;

    const selected = plans.filter((plan) => plan.payment_plan === amount)[0];

    paymentOptionSelected({
      installments_count: parseInt(amount),
      payment_type: paymentTypes[selected.payment_type],
    });

    selectPaymentPlan({ selected });
    shouldCallSummary();
    setIsLoadingSummary(true);
  };

  const validatePreSaleBin = () => {
    if (msci?.preSale) {
      const preSaleBines = Object.values(msci.preSale.bines);
      const index = preSaleBines.findIndex((bin) =>
        temporalBin.includes(bin.bin),
      );
      if (index >= 0) {
        showPreSaleBinError(false);
        setPreSaleBinValid(true);
      } else {
        setPreSaleBinValid(false);
        showPreSaleBinError(true);
      }
    }
  };

  const handleShowedSelector = (value) => {
    handleShowed(value);
  };

  const minPurchase = () => {
    if (msci.availableBin && msci?.msi) {
      if (Object.keys(msci.msi.bines).includes(msci.availableBin)) {
        return msci.msi.minimunPurchase;
      }
    }

    return 300;
  };

  /**/

  useEffect(() => {
    setShowSelector(
      (msci?.msiAvailablePlans ||
        msci?.mciAvailablePlans ||
        (msci?.availableBin &&
          msci.showed &&
          booking?.resume?.total < minPurchase())) &&
        !msci.showPreSaleBinError,
    );
  }, [
    msci?.msiAvailablePlans,
    msci?.mciAvailablePlans,
    msci?.availableBin,
    msci?.showed,
    msci?.showPreSaleBinError,
    booking?.resume?.total,
  ]);

  /*
   * cuando se ha verificado un bin y se ha almacenado un valor
   * de 6 digitos se llama la validación de bin y se valida si es
   * valido para la preventa
   */

  useEffect(() => {
    if (temporalBin) {
      validateBin();
      validatePreSaleBin();
    }
  }, [temporalBin]);

  /*
   * limpia el valor de meses seleccionados cuando se aplica
   * un cupón de descuento o cuando se selecciona/deselecciona
   * ticket protection, siempre y cuando exista un bin disponible
   */
  useEffect(() => {
    const reset = () => {
      if (!msci.availableBin) return;
      resetAndCleanSelected();
      shouldCallSummary();
      setIsLoadingSummary(true);
      return;
    };

    reset();
  }, [booking?.resume?.discount, ticketInsurance?.selected]);

  /*
   * evita que al montarse el componente se muestre el error que el bin seleccionado
   * no esta permitido en la preventa
   * limpia el bin y planes disponibles en la carga de la pagina
   * esto evita que se muestre el selector al recargar la página o regresar a ella
   */
  useEffect(() => {
    if (router.pathname === '/payment') {
      showPreSaleBinError(false);
      resetPlans();
      window.onbeforeunload = () => {
        resetPlans();
      };
      window.addEventListener('popstate', () => {
        resetPlans();
      });

      return () => {
        window.removeEventListener('popstate', resetPlans);
      };
    }
  }, [router.pathname]);

  return {
    verifyBin,
    setBin,
    setAvailablePlans,
    handleSelectedPlan,
    handleShowedSelector,
    showSelector,
    minPurchase,
    msi: msci,
  };
}
